import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import DropdownMenu from "../DropDownMenu/dropdownmenu";
import "./PageBreadcrumb.scss";

/**
 * Common breadcrumb bar with optional Actions dropdown.
 * @param {Array<{ label: React.ReactNode, path?: string }>} items - Breadcrumb items. Last item usually has no path (current page).
 * @param {Array<{ icon: string, label: string, onClick: () => void }>} [actions] - Optional dropdown items (Refresh, Add User, etc.).
 * @param {React.ReactNode} [preActionsExtras] - Optional controls before Actions (e.g. gate badge).
 * @param {React.ReactNode} [toolbarExtras] - Optional controls after Actions (inline with breadcrumb).
 * @param {React.ReactNode} [trailingExtras] - Optional controls aligned to the far right (e.g. Guide).
 * @param {boolean} [forceActionsDropdown] - Always show "Actions" menu even for a single action.
 */
const PageBreadcrumb = ({
  items = [],
  actions = [],
  preActionsExtras = null,
  toolbarExtras = null,
  trailingExtras = null,
  forceActionsDropdown = false,
}) => {
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const actionsDropdownRef = useRef(null);
  const useDropdown =
    actions.length > 1 || (forceActionsDropdown && actions.length > 0);

  return (
    <div className="page-breadcrumb d-flex align-items-center gap-1 mb-3 flex-wrap">
      <nav aria-label="breadcrumb" className="d-flex align-items-center breadcrumb-bar">
        <ol className="breadcrumb mb-0">
          {items.map((item, index) => (
            <li
              key={index}
              className={`breadcrumb-item${item.path == null ? " active" : ""}`}
              aria-current={item.path == null ? "page" : undefined}
            >
              {item.path != null ? (
                <Link to={item.path} className="text-decoration-none">
                  {item.label}
                </Link>
              ) : (
                item.label
              )}
            </li>
          ))}
        </ol>
      </nav>
      {(preActionsExtras != null || actions.length > 0 || toolbarExtras != null) && (
        <div className="d-flex align-items-center gap-1 flex-wrap page-breadcrumb__toolbar">
          {preActionsExtras}
          {!useDropdown && actions.length === 1 && (
            <button
              type="button"
              className="actions-trigger"
              onClick={() => actions[0].onClick?.()}
            >
              {actions[0].icon && <i className={`${actions[0].icon} me-1`} />}
              {actions[0].label}
            </button>
          )}
          {useDropdown && (
            <div className="position-relative">
              <button
                ref={actionsDropdownRef}
                type="button"
                className="actions-trigger"
                onClick={() => setShowActionsDropdown((prev) => !prev)}
                aria-expanded={showActionsDropdown}
                aria-haspopup="true"
              >
                Actions
                <i className={`fas fa-chevron-down ${showActionsDropdown ? "rotate-180" : ""}`}></i>
              </button>
              <DropdownMenu
                items={actions.map((action) => ({
                  ...action,
                  onClick: () => {
                    setShowActionsDropdown(false);
                    action.onClick?.();
                  },
                }))}
                show={showActionsDropdown}
                onClose={() => setShowActionsDropdown(false)}
                trigger={actionsDropdownRef}
              />
            </div>
          )}
          {toolbarExtras}
        </div>
      )}
      {trailingExtras != null ? (
        <div className="page-breadcrumb__trailing ms-auto">{trailingExtras}</div>
      ) : null}
    </div>
  );
};

export default PageBreadcrumb;
